import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Building2, 
  Users,
  Menu,
  X,
  Sun,
  Moon,
  LogOut,
  ArrowLeft,
  Shield,
  Layers
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { path: "/admin", icon: LayoutDashboard, label: "OVERVIEW", end: true },
  { path: "/admin/categories", icon: Layers, label: "CATEGORIES" },
  { path: "/admin/businesses", icon: Building2, label: "COMPANIES" },
  { path: "/admin/users", icon: Users, label: "USERS" },
];

export const AdminLayout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isDark = theme === "dark";
  const showBackButton = location.pathname !== "/admin";

  const handleLogout = () => {
    logout();
    window.location.href = "/login";
  };

  return (
    <div className={`flex min-h-screen ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
      {/* Desktop Sidebar - Fixed */}
      <aside className={`hidden lg:flex flex-col w-64 border-r fixed top-0 left-0 h-screen ${isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'}`}>
        <div className={`p-6 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center shadow-md ${isDark ? 'bg-purple-900' : 'bg-purple-100'}`}>
              <Shield className={`w-7 h-7 ${isDark ? 'text-purple-300' : 'text-purple-600'}`} />
            </div>
            <div>
              <h1 className={`font-bold text-lg tracking-tight ${isDark ? 'text-white' : 'text-slate-800'}`}>
                ADMIN
              </h1>
              <p className={`text-xs uppercase tracking-widest ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Platform Control
              </p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 text-sm font-medium tracking-wider transition-colors duration-200 rounded-lg ${
                      isActive
                        ? `bg-purple-600 text-white shadow-md`
                        : `${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className={`p-4 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className={`px-4 py-2 mb-3 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-purple-50'}`}>
            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-purple-600'}`}>Logged in as</p>
            <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>{user?.name || 'Admin'}</p>
          </div>
          
          {/* Theme Toggle */}
          <Button
            variant="outline"
            onClick={toggleTheme}
            className={`w-full mb-3 justify-start gap-3 ${isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200 text-slate-600 hover:bg-slate-100'}`}
            data-testid="theme-toggle"
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {isDark ? "Light Mode" : "Dark Mode"}
          </Button>
          
          {/* Logout */}
          <Button
            variant="outline"
            onClick={handleLogout}
            className={`w-full justify-start gap-3 text-red-500 hover:text-red-600 ${isDark ? 'border-slate-700 hover:bg-slate-800' : 'border-slate-200 hover:bg-red-50'}`}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Log Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 z-50 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'} border-b`}>
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            {showBackButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className={`mr-2 ${isDark ? 'text-white hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-purple-900' : 'bg-purple-100'}`}>
              <Shield className={`w-6 h-6 ${isDark ? 'text-purple-300' : 'text-purple-600'}`} />
            </div>
            <span className={`font-bold uppercase tracking-tight ${isDark ? 'text-white' : 'text-slate-800'}`}>
              ADMIN
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={isDark ? 'text-slate-300' : 'text-slate-600'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className={`p-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'} border-t`}>
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    end={item.end}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 text-sm font-medium tracking-wider transition-colors duration-200 rounded-lg ${
                        isActive
                          ? "bg-purple-600 text-white"
                          : `${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
              <li>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-4 py-3 text-sm font-medium tracking-wider text-red-500 hover:bg-red-50 dark:hover:bg-slate-700 rounded-lg w-full"
                >
                  <LogOut className="w-5 h-5" />
                  LOG OUT
                </button>
              </li>
            </ul>
          </nav>
        )}
      </div>

      {/* Main Content - with left margin for fixed sidebar */}
      <main className={`flex-1 lg:ml-64 lg:p-8 p-4 pt-20 lg:pt-8 overflow-auto ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
        {showBackButton && (
          <div className="hidden lg:block mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className={`gap-2 ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
